<template>
  <b-modal
    :visible="visible"
    @change="onVisibleChange"
    :title="modalTitle"
    size="lg"
    hide-footer
    centered
  >
    <div v-if="loading" class="text-center py-5">
      <b-spinner></b-spinner>
    </div>

    <template v-else>
      <!-- Step 1: pick a day -->
      <div v-if="step === 'day'">
        <p class="text-muted">{{ $t('series.pickDay') }}</p>
        <div v-if="days.length === 0" class="text-center text-muted py-3">
          {{ $t('series.noSessionsForDay') }}
        </div>
        <div v-else class="series-days">
          <button
            v-for="day in days"
            :key="day.date"
            class="series-day-tile"
            :disabled="day.totalAvailableSeats === 0"
            @click="selectDay(day)"
          >
            <span class="series-day-tile__date">{{ formatDayLabel(day.date) }}</span>
            <span class="series-day-tile__count">
              {{ $tc('series.sessionsAvailable', day.availableSessionsCount, { count: day.availableSessionsCount }) }}
            </span>
          </button>
        </div>
      </div>

      <!-- Step 2: pick a session -->
      <div v-else-if="step === 'session'">
        <a class="series-back-link" @click="step = 'day'">&larr; {{ $t('series.pickDay') }}</a>
        <p class="text-muted">{{ formatDayLabel(selectedDay.date) }} — {{ $t('series.pickSession') }}</p>
        <div v-if="sessions.length === 0" class="text-center text-muted py-3">
          {{ $t('series.noSessionsForDay') }}
        </div>
        <div v-else class="series-sessions">
          <button
            v-for="session in sessions"
            :key="session.uuid"
            class="series-session-tile"
            :disabled="session.availableSeats === 0"
            @click="selectSession(session)"
          >
            <span class="series-session-tile__time">{{ formatSessionTime(session.datetime) }}</span>
            <span class="series-session-tile__seats">
              {{ session.availableSeats }} / {{ session.seats }}
            </span>
          </button>
        </div>
      </div>
    </template>
  </b-modal>
</template>

<script>
import ApiService from '../../services/api'

export default {
  name: 'SeriesPickerModal',

  props: {
    workshop: {
      type: Object,
      default: null
    },
    visible: {
      type: Boolean,
      default: false
    },
    privateAccessToken: {
      type: String,
      default: null
    }
  },

  data() {
    return {
      loading: false,
      step: 'day',
      days: [],
      sessions: [],
      selectedDay: null
    }
  },

  computed: {
    modalTitle() {
      return this.workshop ? this.workshop.name : ''
    }
  },

  watch: {
    visible(newVal) {
      if (newVal && this.workshop) {
        this.fetchDays()
      }
    }
  },

  methods: {
    onVisibleChange(value) {
      this.$emit('update:visible', value)
      if (!value) {
        this.step = 'day'
        this.days = []
        this.sessions = []
        this.selectedDay = null
      }
    },

    async fetchDays() {
      this.loading = true
      this.step = 'day'
      try {
        const response = await ApiService.getWorkshopDays(this.workshop.uuid, this.privateAccessToken)
        this.days = response.data || []
      } catch (e) {
        this.days = []
      } finally {
        this.loading = false
      }
    },

    async selectDay(day) {
      this.selectedDay = day
      this.loading = true
      this.step = 'session'
      try {
        const response = await ApiService.getWorkshopDaySessions(
          this.workshop.uuid,
          day.date,
          this.privateAccessToken
        )
        this.sessions = response.data || []
      } catch (e) {
        this.sessions = []
      } finally {
        this.loading = false
      }
    },

    selectSession(session) {
      // Hand off to the parent: it will open the regular reservation modal with this child workshop UUID.
      this.$emit('session-selected', session)
      this.onVisibleChange(false)
    },

    formatDayLabel(date) {
      return this.$dayjs(date).format('dddd D MMMM')
    },

    formatSessionTime(datetime) {
      return this.$dayjs(datetime).format('HH:mm')
    }
  }
}
</script>

<style lang="scss" scoped>
.series-days,
.series-sessions {
  display: grid;
  gap: 0.75rem;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
}

.series-day-tile,
.series-session-tile {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 0.75rem 0.5rem;
  border: 1px solid #ddd;
  background: #fff;
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.15s;

  &:hover:not([disabled]) {
    background: #f5f5f5;
  }

  &[disabled] {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.series-day-tile__date,
.series-session-tile__time {
  font-weight: 600;
  font-size: 1rem;
}

.series-day-tile__count,
.series-session-tile__seats {
  font-size: 0.85rem;
  color: #666;
  margin-top: 0.25rem;
}

.series-back-link {
  display: inline-block;
  cursor: pointer;
  color: #0066cc;
  margin-bottom: 0.75rem;
}
</style>
